import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useReferralProgramEdit } from '@/contexts/ReferralProgramEditContext';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { useBrandSelection } from '@/hooks/useBrandSelection';
import { useToast } from '@/hooks/use-toast';
import { useTypedTranslation } from '@/lib/i18n/hooks';
import type { ReferralProgram, ReferralMilestone } from '@/types/referral';
import {
  MilestoneTiersEditor,
  type MilestoneNudgeConfig,
  type MilestoneCelebrationConfig,
} from '@/components/referral/MilestoneTiersEditor';

export function GlobalReferralProgramEditDialog() {
  const { isOpen, editingProgram, closeEditProgram } = useReferralProgramEdit();
  const { currentBrandId } = useBrandSelection();
  const { updateProgram } = useReferralPrograms(currentBrandId);
  const { toast } = useToast();
  const { t } = useTypedTranslation();

  const [form, setForm] = useState<{
    name: string;
    type: ReferralProgram['type'];
    description: string;
    milestones: ReferralMilestone[];
    nudgeConfig: MilestoneNudgeConfig;
    celebrationConfig: MilestoneCelebrationConfig;
  }>({
    name: '',
    type: 'generic',
    description: '',
    milestones: [],
    nudgeConfig: { enabled: false, within: 2, subject: '', content: '' },
    celebrationConfig: { enabled: false, subject: '', content: '' },
  });

  useEffect(() => {
    if (editingProgram && isOpen) {
      const s = (editingProgram.settings as Record<string, unknown>) ?? {};
      setForm({
        name: editingProgram.name,
        type: editingProgram.type,
        description: editingProgram.description ?? '',
        milestones: ((s.milestones as ReferralMilestone[]) ?? []).slice(),
        nudgeConfig: {
          enabled: !!(s.milestone_nudge_enabled),
          within: (s.milestone_nudge_within as number) ?? 2,
          subject: (s.milestone_nudge_subject as string) ?? '',
          content: (s.milestone_nudge_content as string) ?? '',
        },
        celebrationConfig: {
          enabled: !!(s.milestone_celebration_enabled),
          subject: (s.milestone_celebration_subject as string) ?? "Congratulations! You've unlocked {reward}!",
          content: (s.milestone_celebration_content as string) ?? '',
        },
      });
    }
  }, [editingProgram, isOpen]);

  const handleSave = async () => {
    if (!editingProgram) return;
    const baseSettings = (editingProgram.settings as Record<string, unknown>) ?? {};
    const settings =
      form.type === 'milestone'
        ? {
            ...baseSettings,
            milestones: form.milestones,
            milestone_nudge_enabled: form.nudgeConfig.enabled,
            milestone_nudge_within: form.nudgeConfig.within,
            milestone_nudge_subject: form.nudgeConfig.subject,
            milestone_nudge_content: form.nudgeConfig.content,
            milestone_celebration_enabled: form.celebrationConfig.enabled,
            milestone_celebration_subject: form.celebrationConfig.subject,
            milestone_celebration_content: form.celebrationConfig.content,
          }
        : { ...baseSettings, milestones: undefined, milestone_nudge_enabled: false };
    const ok = await updateProgram(editingProgram.id, {
      name: form.name,
      type: form.type,
      description: form.description || undefined,
      settings,
    });
    if (ok) {
      toast({ title: t('referral.common.success'), description: t('referral.programs.programUpdated') });
      closeEditProgram();
    }
  };

  const showMilestoneTab = form.type === 'milestone';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeEditProgram()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>{t('referral.programs.editProgramTitle')}</DialogHeader>
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="general">{t('referral.programs.tabGeneral')}</TabsTrigger>
            {showMilestoneTab && (
              <TabsTrigger value="milestoneTiers">{t('referral.programs.tabMilestoneTiers')}</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="general" className="space-y-4 pt-4">
            <div>
              <Label>{t('referral.programs.name')}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('referral.programs.type')}</Label>
              <Select
                value={form.type}
                onValueChange={(v: ReferralProgram['type']) =>
                  setForm((f) => ({ ...f, type: v, milestones: v === 'milestone' ? f.milestones : [] }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="generic">{t('referral.programs.generic')}</SelectItem>
                  <SelectItem value="milestone">{t('referral.programs.milestone')}</SelectItem>
                  <SelectItem value="waiting_list">{t('referral.programs.waitingList')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('referral.programs.description')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="mt-1"
                rows={3}
              />
            </div>
          </TabsContent>
          {showMilestoneTab && (
            <TabsContent value="milestoneTiers" className="pt-4">
              <MilestoneTiersEditor
                milestones={form.milestones}
                onChange={(m) => setForm((f) => ({ ...f, milestones: m }))}
                nudgeConfig={form.nudgeConfig}
                onNudgeConfigChange={(c) => setForm((f) => ({ ...f, nudgeConfig: c }))}
                celebrationConfig={form.celebrationConfig}
                onCelebrationConfigChange={(c) => setForm((f) => ({ ...f, celebrationConfig: c }))}
                brandId={currentBrandId}
                programId={editingProgram?.id}
              />
            </TabsContent>
          )}
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={closeEditProgram}>
            {t('referral.campaigns.cancel')}
          </Button>
          <Button onClick={handleSave}>{t('referral.lists.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
